import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader, Spinner } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Search, X, Download, RefreshCw } from "lucide-react";

const roleBadge = (role) => {
  const map = {
    super_admin: "bg-kid-teal/15 text-kid-teal",
    admin: "bg-kid-teal/15 text-kid-teal",
    provider: "bg-[#e8d9ff] text-[#6b3fb0]",
    parent: "bg-kid-mist text-kid-muted",
    system: "bg-kid-navy/10 text-kid-navy",
  };
  return map[role] || "bg-kid-mist text-kid-muted";
};

export default function AdminAudit() {
  const [logs, setLogs] = useState([]);
  const [filters, setFilters] = useState({ actors: [], actions: [], target_types: [], roles: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [f, setF] = useState({ actor_id: "", action: "", result: "", target_type: "", role: "", search: "", date_from: "", date_to: "" });

  useEffect(() => {
    api.get("/admin/audit-logs/filters").then((r) => setFilters(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setRefreshing(true);
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    try {
      const { data } = await api.get("/admin/audit-logs", { params });
      setLogs(data);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [f]);

  useEffect(() => {
    const t = setTimeout(load, f.search ? 350 : 0);
    return () => clearTimeout(t);
  }, [load]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const clearAll = () => setF({ actor_id: "", action: "", result: "", target_type: "", role: "", search: "", date_from: "", date_to: "" });
  const activeCount = Object.values(f).filter(Boolean).length;

  const actorLabel = (a) => `${a.name ? a.name + " · " : ""}${a.email || a.id?.slice(0, 8)}${a.role ? " (" + a.role + ")" : ""}`;

  const exportCsv = () => {
    const rows = [["When", "Actor", "Email", "Role", "Action", "Target Type", "Target ID", "Result", "Metadata"]];
    logs.forEach((l) => rows.push([
      new Date(l.created_at).toISOString(), l.actor_name || "", l.actor_email || "", l.actor_role || "",
      l.action || "", l.target_type || "", l.target_id || "", l.result || "",
      JSON.stringify(l.metadata || {}),
    ]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const selectCls = "h-10 rounded-xl border border-kid-line bg-white px-3 text-sm text-kid-navy font-poppins focus:outline-none focus:border-kid-teal";

  if (loading) return <Spinner />;

  return (
    <div>
      <PageHeader eyebrow="Accountability" title="Audit Logs" subtitle="A record of sensitive user and admin actions — filter by who did what." />

      <div className="kid-card p-4 mb-4 space-y-3" data-testid="audit-filters">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-kid-muted" />
            <Input value={f.search} onChange={set("search")} placeholder="Search action, target type or ID…"
                   className="h-10 rounded-xl pl-9" data-testid="audit-search-input" />
          </div>
          <select value={f.actor_id} onChange={set("actor_id")} className={selectCls} data-testid="audit-actor-filter">
            <option value="">All actors</option>
            {filters.actors.map((a) => <option key={a.id} value={a.id}>{actorLabel(a)}</option>)}
          </select>
          <select value={f.role} onChange={set("role")} className={selectCls} data-testid="audit-role-filter">
            <option value="">All roles</option>
            {filters.roles.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <select value={f.action} onChange={set("action")} className={selectCls} data-testid="audit-action-filter">
            <option value="">All actions</option>
            {filters.actions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <select value={f.target_type} onChange={set("target_type")} className={selectCls} data-testid="audit-target-filter">
            <option value="">All targets</option>
            {filters.target_types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={f.result} onChange={set("result")} className={selectCls} data-testid="audit-result-filter">
            <option value="">Any result</option>
            <option value="success">success</option>
            <option value="failure">failure</option>
          </select>
          <div className="flex items-center gap-2 text-sm text-kid-muted">
            <span className="font-poppins">From</span>
            <input type="date" value={f.date_from} onChange={set("date_from")} className={selectCls} data-testid="audit-date-from" />
            <span className="font-poppins">To</span>
            <input type="date" value={f.date_to} onChange={set("date_to")} className={selectCls} data-testid="audit-date-to" />
          </div>
          <div className="flex-1" />
          {activeCount > 0 && (
            <button onClick={clearAll} className="inline-flex items-center gap-1.5 text-sm text-kid-coral font-semibold" data-testid="audit-clear-filters">
              <X size={14} /> Clear ({activeCount})
            </button>
          )}
          <button onClick={load} className="kid-btn kid-btn-ghost h-10 px-3" data-testid="audit-refresh" title="Refresh">
            <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} />
          </button>
          <button onClick={exportCsv} disabled={!logs.length} className="kid-btn kid-btn-ghost h-10 px-3 disabled:opacity-50" data-testid="audit-export-csv">
            <Download size={15} /> CSV
          </button>
        </div>
      </div>

      <div className="text-sm text-kid-muted mb-2 font-poppins" data-testid="audit-count">
        Showing {logs.length} {logs.length === 1 ? "entry" : "entries"}
      </div>

      <div className="kid-card overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-audit-table">
          <thead className="bg-kid-mist text-kid-muted text-left"><tr>
            <th className="px-5 py-3 font-poppins font-semibold">When</th>
            <th className="px-5 py-3">Actor</th>
            <th className="px-5 py-3">Action</th>
            <th className="px-5 py-3">Target</th>
            <th className="px-5 py-3">Result</th>
          </tr></thead>
          <tbody>
            {logs.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-10 text-center text-kid-muted" data-testid="audit-empty">No matching entries.</td></tr>
            )}
            {logs.map((l) => (
              <tr key={l.id} className="border-t border-kid-line align-top" data-testid="admin-audit-row">
                <td className="px-5 py-3 text-kid-muted whitespace-nowrap">{new Date(l.created_at).toLocaleString("en-CA")}</td>
                <td className="px-5 py-3">
                  <div className="font-medium text-kid-navy" data-testid="audit-actor-name">{l.actor_name || l.actor_email || (l.actor_role === "system" ? "System" : "—")}</div>
                  {l.actor_email && <div className="text-xs text-kid-muted">{l.actor_email}</div>}
                  <span className={`inline-block mt-1 text-[.65rem] font-semibold px-2 py-0.5 rounded-full ${roleBadge(l.actor_role)}`}>{l.actor_role}</span>
                </td>
                <td className="px-5 py-3">
                  <div className="font-medium text-kid-navy">{l.action}</div>
                  {l.metadata && Object.keys(l.metadata).length > 0 && (
                    <div className="text-xs text-kid-muted mt-0.5 max-w-[240px] truncate" title={JSON.stringify(l.metadata)}>
                      {Object.entries(l.metadata).map(([k, v]) => `${k}: ${v}`).join(", ")}
                    </div>
                  )}
                </td>
                <td className="px-5 py-3 text-kid-muted">
                  {l.target_type || "—"}
                  {l.target_id && <div className="text-xs text-kid-muted/70 truncate max-w-[160px]" title={l.target_id}>{l.target_id}</div>}
                </td>
                <td className="px-5 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${l.result === "success" ? "bg-kid-green/15 text-[#3f9d5c]" : "bg-kid-coral/12 text-kid-coral"}`}>{l.result}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
